import { IProvider } from '../models/provider.model';
import { ClientReferralMember } from '../models/client-referral-member.model';

/**
 * Derived state describing whether a client has an existing referral-member
 * match with a given provider.
 */
export interface IProviderMatchState {
  /** True when the client has a referral member tied to this provider. */
  hasMatched: boolean;
  /** The matching referral member's id, or undefined when there is no match. */
  clientReferralMemberId: number | undefined;
}

/**
 * Resolves whether a provider is part of the client's care team by matching the
 * provider's account UUID against the client's referral members.
 *
 * Centralizes the provider→CRM reconciliation that was previously duplicated
 * across the landing page and the message-center room. A null/undefined provider
 * always resolves to "no match" so callers can reset their state uniformly.
 *
 * @param provider The resolved provider, or null/undefined when none was found
 * @param members The client's referral members
 * @returns The derived match state (hasMatched + clientReferralMemberId)
 */
export function resolveProviderMatch(
  provider: IProvider | null | undefined,
  members: ClientReferralMember[]
): IProviderMatchState {
  if (!provider) {
    return { hasMatched: false, clientReferralMemberId: undefined };
  }

  const crm = members.find(
    (member) => member.providerPlatformAccountUuid === provider.providerAccountId
  );

  return { hasMatched: !!crm, clientReferralMemberId: crm?.id };
}
